/**
 * The cache tags, declared once so the two apps cannot spell the same tag differently.
 *
 * The reasoning behind the shape — particularly why `Stock` is keyed by shop *and* variant — is
 * in `docs/rtk-query-tags.md`. The short version: completing a sale must invalidate stock and
 * today's sales, not the cache.
 */

import type {
  CategoryId,
  ImportBatchId,
  ProductId,
  PurchaseId,
  RoleId,
  SaleId,
  ShopId,
  SupplierId,
  UserId,
  VariantId,
} from '../types/api.js';
import type { PaymentId, ShiftId } from '../types/payments.js';
import type { ClaimId, StockCountId, TransferId, WriteOffId } from '../types/stockIntegrity.js';
import type { ApprovalRequestId, DisputeId } from '../types/oversight.js';

export const TAGS = [
  'Session',
  'Shop',
  'Variant',
  'Stock',
  'Unit',
  'Sale',
  'SaleList',
  'ReversalRequestList',
  'Receipt',
  'Price',
  'User',
  'UserList',
  'Role',
  'RoleList',
  'PermissionCatalogue',
  'CategoryTree',
  'CategoryAttributes',
  'Product',
  'ProductList',
  'VariantList',
  'Supplier',
  'SupplierList',
  'Purchase',
  'PurchaseList',
  'Movement',
  'ImportBatch',

  // Phase 3. `Payment` and `SalePosition` are separate because they answer different
  // questions and change at different times: a payment row settles once, while a sale's
  // position changes every time anything lands against it.
  'Payment',
  'PaymentList',
  'SalePosition',
  'Unmatched',
  'Shift',
  'ShiftList',
  'CurrentShift',
  'Handover',
  'CashPosition',
  'MpesaEnvironment',

  // Phase 4. `Transfer` and `InTransit` are separate because in-transit is a derived figure across
  // every open transfer, not a property of one: dispatching transfer A changes the in-transit list
  // without changing transfer B, and refetching every transfer to learn that would be absurd.
  // `Discrepancy` is separate again because a discrepancy outlives the receipt that raised it and
  // is worked from its own queue.
  'Transfer',
  'TransferList',
  'InTransit',
  'Discrepancy',
  'StockCount',
  'StockCountList',
  'WriteOff',
  'WriteOffList',
  'FaultyStock',
  'Shrinkage',
  'Claim',
  'ClaimList',
  'Storefront',
  'OnlineListing',
  'OnlineListingList',
  'OnlineOrder',
  'OnlineOrderList',
  'SupplierCredit',
  'Settlement',
  'PriceBulk',

  // Phase 5
  'ReviewItemList',
  'ReviewQueueSummary',
  'Dispute',
  'DisputeList',
  'DisputeNoteList',
  'ApprovalRequest',
  'ApprovalRequestList',
  'BrokerList',
  'CommissionBalance',
  'CommissionStatement',
  'CustomerList',

  // Phase 6 Part A — printing.
  'PrintAgentList',
  'PrinterList',
  'PrintJobList',

  // Phones handed to brokers.
  'BrokerLoanList',
  'AppSettingList',
  'BrokerCommission',
] as const;

export type TagType = (typeof TAGS)[number];

/** The id used for "the whole collection", when a list genuinely must refetch. */
export const LIST = 'LIST' as const;

export const shopTag = (id: ShopId) => ({ type: 'Shop', id }) as const;
export const shopListTag = () => ({ type: 'Shop', id: LIST }) as const;

/** One tag for the whole tree — a category create/rename/move touches the shape everyone reads. */
export const categoryTreeTag = () => ({ type: 'CategoryTree', id: LIST }) as const;
/** Attributes are read per-category (inheritance means a child's list depends on its ancestors too). */
export const categoryAttributesTag = (id: CategoryId) =>
  ({ type: 'CategoryAttributes', id }) as const;

export const productTag = (id: ProductId) => ({ type: 'Product', id }) as const;
export const productListTag = () => ({ type: 'ProductList', id: LIST }) as const;

export const variantTag = (id: VariantId) => ({ type: 'Variant', id }) as const;
export const variantListTag = () => ({ type: 'VariantList', id: LIST }) as const;

/** Prices are read per-variant, and per-variant is also all that ever needs to refetch. */
export const priceListTag = (variantId: VariantId) => ({ type: 'Price', id: variantId }) as const;

export const supplierTag = (id: SupplierId) => ({ type: 'Supplier', id }) as const;
export const supplierListTag = () => ({ type: 'SupplierList', id: LIST }) as const;

export const purchaseTag = (id: PurchaseId) => ({ type: 'Purchase', id }) as const;
export const purchaseListTag = () => ({ type: 'PurchaseList', id: LIST }) as const;

/** One tag for the whole stock list — an adjustment or a reconcile can move any row in it. */
export const stockListTag = () => ({ type: 'Stock', id: LIST }) as const;
export const movementListTag = () => ({ type: 'Movement', id: LIST }) as const;

export const importBatchTag = (id: ImportBatchId) => ({ type: 'ImportBatch', id }) as const;

/** Composite so a sale in Mombasa never invalidates the Nairobi till's cached quantity. */
export const stockTag = (shopId: ShopId, variantId: VariantId) =>
  ({ type: 'Stock', id: `${shopId}:${variantId}` }) as const;

export const unitTag = (shopId: ShopId, variantId: VariantId) =>
  ({ type: 'Unit', id: `${shopId}:${variantId}` }) as const;

/**
 * `scope` is `today`, `mine`, or a stable hash of a dashboard filter. A till invalidating
 * `today` and `mine` leaves the dashboard's filtered reports alone, which is correct: a report
 * nobody is looking at does not need refreshing.
 */
export const saleListTag = (shopId: ShopId, scope: string) =>
  ({ type: 'SaleList', id: `${shopId}:${scope}` }) as const;

export const saleTag = (id: SaleId) => ({ type: 'Sale', id }) as const;
export const receiptTag = (id: SaleId) => ({ type: 'Receipt', id }) as const;

export const userTag = (id: UserId) => ({ type: 'User', id }) as const;
/** The whole list — user creation and deactivation are rare enough that a per-shop scope buys nothing. */
export const userListTag = () => ({ type: 'UserList', id: LIST }) as const;

export const roleTag = (id: RoleId) => ({ type: 'Role', id }) as const;
export const roleListTag = () => ({ type: 'RoleList', id: LIST }) as const;
/** The permission catalogue is effectively static — one tag covers the whole thing. */
export const permissionCatalogueTag = () => ({ type: 'PermissionCatalogue', id: LIST }) as const;

// ── Phase 3 ─────────────────────────────────────────────────────────────────

export const paymentTag = (id: PaymentId) => ({ type: 'Payment', id }) as const;

/** `scope` is `recent`, `reconciliation`, `credit`, or a hash of a dashboard filter. */
export const paymentListTag = (shopId: ShopId, scope: string) =>
  ({ type: 'PaymentList', id: `${shopId}:${scope}` }) as const;

/**
 * A sale's money position, keyed by the sale. Separate from `saleTag` on purpose: a payment
 * settling changes what the sale has been paid without changing a single thing about the sale
 * itself, and refetching the sale would be a lie about what happened.
 */
export const salePositionTag = (saleId: SaleId) => ({ type: 'SalePosition', id: saleId }) as const;

export const unmatchedListTag = (shopId: ShopId) => ({ type: 'Unmatched', id: shopId }) as const;

export const shiftTag = (id: ShiftId) => ({ type: 'Shift', id }) as const;
export const shiftListTag = (shopId: ShopId) => ({ type: 'ShiftList', id: shopId }) as const;
export const handoverListTag = (shopId: ShopId) => ({ type: 'Handover', id: shopId }) as const;

/**
 * Who is holding what, right now. Invalidated by every cash movement — a payment, a payout, a
 * handover, a declaration — because all four change the same number.
 */
export const cashPositionTag = (shopId: ShopId) => ({ type: 'CashPosition', id: shopId }) as const;

// ── Phase 4 ─────────────────────────────────────────────────────────────────

export const transferTag = (id: TransferId) => ({ type: 'Transfer', id }) as const;

/**
 * One tag for the whole list rather than per shop, because a transfer belongs to *two* shops and
 * appears in both of their lists. A per-shop key would need invalidating twice and would be
 * forgotten once.
 */
export const transferListTag = () => ({ type: 'TransferList', id: LIST }) as const;

/** Derived across every open transfer, so there is nothing finer to key it by. */
export const inTransitTag = () => ({ type: 'InTransit', id: LIST }) as const;
export const discrepancyListTag = () => ({ type: 'Discrepancy', id: LIST }) as const;

export const stockCountTag = (id: StockCountId) => ({ type: 'StockCount', id }) as const;
export const stockCountListTag = () => ({ type: 'StockCountList', id: LIST }) as const;

export const writeOffTag = (id: WriteOffId) => ({ type: 'WriteOff', id }) as const;
export const writeOffListTag = () => ({ type: 'WriteOffList', id: LIST }) as const;

/** Per shop: one shop moving a cover to faulty says nothing about another shop's register. */
export const faultyStockTag = (shopId: ShopId) => ({ type: 'FaultyStock', id: shopId }) as const;

/** The shrinkage report is a period aggregate; any approved write-off can move it. */
export const shrinkageTag = () => ({ type: 'Shrinkage', id: LIST }) as const;

export const claimTag = (id: ClaimId) => ({ type: 'Claim', id }) as const;
export const claimListTag = () => ({ type: 'ClaimList', id: LIST }) as const;

/** Per supplier, which is the only way anybody ever asks "what do they still owe us?". */
export const supplierCreditListTag = (supplierId: SupplierId) =>
  ({ type: 'SupplierCredit', id: supplierId }) as const;

/** Like transfers, a settlement belongs to two shops — so one list tag, not one per shop. */
export const settlementListTag = () => ({ type: 'Settlement', id: LIST }) as const;

export const priceBulkTag = (batchId: string) => ({ type: 'PriceBulk', id: batchId }) as const;

// ── Phase 5 ───────────────────────────────────────────────────────────────────

export const reviewItemListTag = () => ({ type: 'ReviewItemList', id: LIST }) as const;
export const reviewQueueSummaryTag = () => ({ type: 'ReviewQueueSummary', id: LIST }) as const;

export const disputeTag = (id: DisputeId) => ({ type: 'Dispute', id }) as const;
export const disputeListTag = () => ({ type: 'DisputeList', id: LIST }) as const;
export const disputeNoteListTag = (id: DisputeId) => ({ type: 'DisputeNoteList', id }) as const;

export const approvalRequestTag = (id: ApprovalRequestId) =>
  ({ type: 'ApprovalRequest', id }) as const;
export const approvalRequestListTag = () => ({ type: 'ApprovalRequestList', id: LIST }) as const;

export const brokerListTag = () => ({ type: 'BrokerList', id: LIST }) as const;
export const commissionBalanceTag = (subjectId: string) =>
  ({ type: 'CommissionBalance', id: subjectId }) as const;
export const commissionStatementTag = (subjectId: string) =>
  ({ type: 'CommissionStatement', id: subjectId }) as const;

export const customerListTag = () => ({ type: 'CustomerList', id: LIST }) as const;

// ── Phase 6 Part A ────────────────────────────────────────────────────────────

/** One tag for the whole list — issuing or revoking an agent is rare enough to not need scoping. */
export const printAgentListTag = () => ({ type: 'PrintAgentList', id: LIST }) as const;

/** One tag for the whole list — a shop's printers are few, and a printer's agent can change. */
export const printerListTag = () => ({ type: 'PrinterList', id: LIST }) as const;

/** One tag for the whole list; the queue is read live and filtered client-side by status/printer. */
export const printJobListTag = () => ({ type: 'PrintJobList', id: LIST }) as const;

/** One tag for every shop's list — handing out or taking back a phone moves one row in it. */
export const brokerLoanListTag = () => ({ type: 'BrokerLoanList', id: LIST }) as const;

/** Business rules read by key; one list, since a save changes one row of it. */
export const appSettingListTag = () => ({ type: 'AppSettingList', id: LIST }) as const;

/** A broker's rates and payment history — one tag, since setting a rate or paying touches both views. */
export const brokerCommissionTag = (brokerId: string) =>
  ({ type: 'BrokerCommission', id: brokerId }) as const;

// ── Online store ────────────────────────────────────────────────────────────
export const storefrontTag = () => ({ type: 'Storefront', id: 'CURRENT' }) as const;
export const onlineListingTag = (id: string) => ({ type: 'OnlineListing', id }) as const;
export const onlineListingListTag = () => ({ type: 'OnlineListingList', id: LIST }) as const;
export const onlineOrderTag = (id: string) => ({ type: 'OnlineOrder', id }) as const;
export const onlineOrderListTag = () => ({ type: 'OnlineOrderList', id: LIST }) as const;
